"use client";

import type { QueryClient } from "@tanstack/react-query";
import { MODEL_QUERY_FRAGMENTS, type InteractionModel } from "./models";

/**
 * Writes a confirmed like/watchlist result back into the React Query cache, for
 * the one entity that changed.
 *
 * Why a targeted write instead of `invalidateQueries`: invalidating a list key
 * refetches every query under it and visibly re-renders a whole grid for one
 * bookmark click (the reason the previous collections-only mutation hook
 * explicitly avoided it). `setQueriesData` mutates the cached page in place, so
 * the UI updates instantly with no request and no list re-fetch.
 *
 * Why this is safe to key on `id` alone: candidate objects must (a) live under a
 * query whose first key segment matches this model type's fragments, and (b)
 * already carry a `current_user` object. An nft and a blog can both be id 5, but
 * they never share a query — see MODEL_QUERY_FRAGMENTS.
 *
 * The point of writing to the cache is that it lets the optimistic override in
 * the toggle hooks retire: once the cache holds the new value, the value flows
 * back to every card as a prop, and the hooks' "server value wins when it
 * differs from what we based the optimistic flip on" rule adopts it. Without
 * this, the override would have to stay in place to avoid being flipped back by
 * the stale cached page.
 */
export type InteractionCachePatch = {
  like?: boolean;
  watchlist?: boolean;
  likeCount?: number;
};

function matchesModel(queryKey: readonly unknown[], modelType: InteractionModel): boolean {
  const fragments = MODEL_QUERY_FRAGMENTS[modelType];
  if (fragments.length === 0) return false;
  const head = String(queryKey[0] ?? "").toLowerCase();
  return fragments.some((fragment) => head.includes(fragment));
}

function sameEntity(node: Record<string, any>, modelId: number | string): boolean {
  const target = String(modelId);
  // Drops have no numeric id and are addressed by slug, so accept either.
  return String(node.id ?? "") === target || String(node.slug ?? "") === target;
}

function applyPatch(node: Record<string, any>, patch: InteractionCachePatch) {
  const next: Record<string, any> = { ...node };

  next.current_user = { ...(node.current_user ?? {}) };
  if (patch.like !== undefined) next.current_user.like = patch.like;
  if (patch.watchlist !== undefined) next.current_user.watchlist = patch.watchlist;

  if (patch.likeCount !== undefined) {
    next.likes = { ...(node.likes ?? {}), count: patch.likeCount };
  }

  return next;
}

/**
 * Depth-limited structural walk. Cached payloads here are shallow envelopes
 * (`{ data: { items: [...] } }`, `{ galleries: [...] }`, `{ data: { list: [...] } }`),
 * so a small cap keeps this cheap and stops it recursing into item internals.
 */
function patchNode(
  value: unknown,
  modelId: number | string,
  patch: InteractionCachePatch,
  depth: number
): { value: unknown; changed: boolean } {
  if (depth > 4 || value == null || typeof value !== "object") {
    return { value, changed: false };
  }

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const result = patchNode(entry, modelId, patch, depth + 1);
      if (result.changed) changed = true;
      return result.value;
    });
    return changed ? { value: next, changed: true } : { value, changed: false };
  }

  const node = value as Record<string, any>;

  // Only objects that already expose `current_user` are interaction-bearing
  // entities; anything else (holders, offers, activity rows) is left alone.
  if (node.current_user && typeof node.current_user === "object" && sameEntity(node, modelId)) {
    return { value: applyPatch(node, patch), changed: true };
  }

  let changed = false;
  const next: Record<string, any> = { ...node };
  for (const key of Object.keys(node)) {
    const result = patchNode(node[key], modelId, patch, depth + 1);
    if (result.changed) {
      changed = true;
      next[key] = result.value;
    }
  }
  return changed ? { value: next, changed: true } : { value, changed: false };
}

export function syncInteractionCache(
  queryClient: QueryClient,
  modelType: InteractionModel,
  modelId: number | string,
  patch: InteractionCachePatch
): void {
  queryClient.setQueriesData(
    { predicate: (query) => matchesModel(query.queryKey, modelType) },
    (current: unknown) => {
      const result = patchNode(current, modelId, patch, 0);
      // Returning the identical reference tells React Query nothing changed, so
      // untouched queries don't notify their observers.
      return result.changed ? result.value : current;
    }
  );
}
